import { TSearchRequest } from '@/components';
import { PageSize, ROUTERS, SearchFilterTypes } from '@/constant';
import { ESearchType as ESearchType_INQUIRY_BOARD } from '@/pages/commons/bb1-01/bb1-01.type';
import { ESearchType as ESearchType_AM } from '@/pages/workers/am1-01/am1-01.type';
import { ESearchType } from '@/pages/workers/ar1-01/ar1-01.type';
import { ESearchType as ESearchType_FR } from '@/pages/workers/fr1-01/fr1-01.type';
import {
	CommonInquiryBoardActions,
	CommonInquiryBoardAnswerActions,
	CommonNoticeActions,
	CommonNoticeReceiveActions,
	DeviceLogActions,
	DeviceMobileActions,
	DeviceTabletActions,
	ForeignApplicantActions,
	ForeignProgramActions,
	ForeignSystemUnitActions,
	ForeignUserActions,
	MSelectLodgingPopupActions,
	SystemUnitActions,
	SystemUserActions,
	WorkerAssignmentActions,
	WorkerAssignmentFranchiseActions,
	WorkerAssignmentPublicActions,
	WorkerAuditMonitoringActions,
	WorkerChangeWorkPlaceActions,
	WorkerDocumentActions,
	WorkerExtensionStayActions,
	WorkerFranchiseActions,
	WorkerLodgingActions,
	WorkerProgramActions,
	WorkerProgramStatusActions,
	WorkerPublicSalaryActions,
	WorkerSalaryActions,
	WorkerUnauthorizedDepartureActions,
	WorkerUserActions,
} from '@/stores';
import { SystemUseTipActions } from '@/stores/system-use-tip';
import { WorkerApplicantActions } from '@/stores/worker-applicant';
import { WorkerRecommenderActions } from '@/stores/worker-recommender';
import { WorkerUseTipActions } from '@/stores/worker-use-tip';
import {
	convertDocumentTypeValue,
	insertObjectIfV2,
	putKeysOutFromSearchTypes,
} from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isNotEmpty, omit } from 'ramda';

type UseSearchFilterSubmitProps<T extends SearchFilterTypes> = {
	searchFilterType?: T;
	pagination?: {
		page?: number;
		pageSize?: PageSize;
	};
	initialParams?: any;
};

export const useSearchFilterSubmit = <T extends SearchFilterTypes>(
	props: UseSearchFilterSubmitProps<T>
) => {
	const { pathname } = useLocation();

	const { searchFilterType, pagination, initialParams } = props;
	const dispatch = useDispatch();

	const onSubmit = useCallback(
		(
			values: TSearchRequest,
			callback?: (props?: any | { data?: any[]; error?: any }) => void
		) => {
			if (!searchFilterType) return;

			const baseData: TSearchRequest = {
				...values,
				page: values?.page || pagination?.page || 1,
				pageSize: values?.pageSize || pagination?.pageSize || 10,
				searchTypes: [
					...(initialParams?.searchTypes || []),
					...(values?.searchTypes || []),
				],
				...insertObjectIfV2(isNotEmpty(initialParams), {
					...omit(['page', 'pageSize', 'searchTypes'], initialParams),
				}),
			};

			switch (searchFilterType) {
				case SearchFilterTypes.NOTICE: {
					dispatch(
						CommonNoticeActions.getCommonNoticeListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.NOTICE_RECEIVE: {
					dispatch(
						CommonNoticeReceiveActions.getCommonNoticeReceiveListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.INQUIRY_BOARD: {
					const newBaseData = putKeysOutFromSearchTypes({
						keys: [ESearchType_INQUIRY_BOARD.STATUS],
						searchTypes: baseData?.searchTypes,
					});
					dispatch(
						CommonInquiryBoardActions.getCommonInquiryBoardListRequest({
							...newBaseData,
							...omit(['searchTypes'], baseData),
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.INQUIRY_BOARD_ANSWER: {
					const newBaseData = putKeysOutFromSearchTypes({
						keys: [ESearchType_INQUIRY_BOARD.STATUS],
						searchTypes: baseData?.searchTypes,
					});
					dispatch(
						CommonInquiryBoardAnswerActions.getCommonInquiryBoardAnswerListRequest(
							{
								...newBaseData,
								...omit(['searchTypes'], baseData),
							}
						)
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.UM1_01: {
					dispatch(
						SystemUserActions.getSystemUserListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.MM1_01: {
					dispatch(
						WorkerUserActions.getWorkerUserListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.AR1_01: {
					if (pathname === ROUTERS.ED1_01) {
						dispatch(
							WorkerApplicantActions.getWorkerApplicantListRequest({
								...omit(['searchTypes'], baseData),
								pathname: ROUTERS.ED1_01,
							})
						);
					} else {
						const newBaseData = putKeysOutFromSearchTypes({
							keys: [ESearchType.DEPARTURE_DATE_COUNT],
							searchTypes: baseData?.searchTypes,
						});

						if (baseData?.allData) {
							dispatch(
								WorkerApplicantActions.getWorkerApplicantExcelListRequest({
									...newBaseData,
									...omit(['searchTypes'], baseData),
									callback,
								})
							);
							return;
						}
						dispatch(
							WorkerApplicantActions.getWorkerApplicantListRequest({
								...newBaseData,
								...omit(['searchTypes'], baseData),
								exportExcel: false,
							})
						);
					}
					callback?.();
					break;
				}

				case SearchFilterTypes.LM1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerLodgingActions.getWorkerLodgingExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerLodgingActions.getWorkerLodgingListRequest({
							...baseData,
							exportExcel: false,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.PA4_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerSalaryActions.getWorkerSalaryExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerSalaryActions.getWorkerSalaryListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.FR1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerFranchiseActions.getWorkerFranchiseExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerFranchiseActions.getWorkerFranchiseListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.BM1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerUnauthorizedDepartureActions.getWorkerUnauthorizedDepartureExcelListRequest(
								{
									...baseData,
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerUnauthorizedDepartureActions.getWorkerUnauthorizedDepartureListRequest(
							{
								...baseData,
							}
						)
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.DM1_01: {
					const newBaseData = isMobile
						? {
								searchTypes: baseData?.searchTypes?.map((e) => ({
									...e,
									searchValue: convertDocumentTypeValue(
										e?.searchValue as string
									),
								})),
							}
						: {};

					dispatch(
						WorkerDocumentActions.getWorkerDocumentListRequest({
							...baseData,
							...newBaseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.PM1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerProgramActions.getWorkerProgramExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerProgramActions.getWorkerProgramListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.PM2_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerProgramStatusActions.getWorkerProgramStatusExcelListRequest(
								{
									...baseData,
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerProgramStatusActions.getWorkerProgramStatusListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.CM1_01: {
					dispatch(
						SystemUnitActions.getSystemUnitListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.RM1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerRecommenderActions.getWorkerRecommenderExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerRecommenderActions.getWorkerRecommenderListRequest({
							...baseData,
						})
					);
					break;
				}

				case SearchFilterTypes.WC1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerChangeWorkPlaceActions.getWorkerChangeWorkPlaceExcelListRequest(
								{
									...baseData,
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerChangeWorkPlaceActions.getWorkerChangeWorkPlaceListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.EM1_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerExtensionStayActions.getWorkerExtensionStayExcelListRequest(
								{
									...baseData,
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerExtensionStayActions.getWorkerExtensionStayListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.AM1_01: {
					const newBaseData = putKeysOutFromSearchTypes({
						keys: [ESearchType_AM.IS_CURRENT],
						searchTypes: baseData?.searchTypes,
					});

					const newFranchiseSearchTypes = putKeysOutFromSearchTypes({
						keys: [ESearchType_FR.IS_LACK],
						searchTypes: baseData?.franchiseSearchTypes,
						keySearchNew: 'franchiseSearchTypes',
					});

					if (baseData?.allData) {
						dispatch(
							WorkerAssignmentFranchiseActions.getWorkerAssignmentFranchiseExcelListRequest(
								{
									...newBaseData,
									...newFranchiseSearchTypes,
									...omit(['searchTypes', 'franchiseSearchTypes'], baseData),
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerAssignmentFranchiseActions.getWorkerAssignmentFranchiseListRequest(
							{
								...newBaseData,
								...newFranchiseSearchTypes,
								...omit(['searchTypes', 'franchiseSearchTypes'], baseData),
								exportExcel: false,
							}
						)
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.PA1_01: {
					const newFranchiseSearchTypes = putKeysOutFromSearchTypes({
						keys: [ESearchType_FR.IS_LACK],
						searchTypes: baseData?.franchiseSearchTypes,
						keySearchNew: 'franchiseSearchTypes',
					});

					if (baseData?.allData) {
						dispatch(
							WorkerAssignmentPublicActions.getWorkerAssignmentPublicExcelListRequest(
								{
									...newFranchiseSearchTypes,
									...omit(['franchiseSearchTypes'], baseData),
									callback,
								}
							)
						);
						return;
					}
					dispatch(
						WorkerAssignmentPublicActions.getWorkerAssignmentPublicListRequest({
							...newFranchiseSearchTypes,
							...omit(['franchiseSearchTypes'], baseData),
							exportExcel: false,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.AM3_01: {
					const newBaseData = putKeysOutFromSearchTypes({
						keys: [ESearchType_AM.IS_CURRENT],
						searchTypes: baseData?.searchTypes,
					});

					const newFranchiseSearchTypes = putKeysOutFromSearchTypes({
						keys: [ESearchType_FR.IS_LACK],
						searchTypes: baseData?.franchiseSearchTypes,
						keySearchNew: 'franchiseSearchTypes',
					});

					if (baseData?.allData) {
						dispatch(
							WorkerAssignmentActions.getWorkerAssignmentExcelListRequest({
								...newBaseData,
								...newFranchiseSearchTypes,
								...omit(['searchTypes', 'franchiseSearchTypes'], baseData),
								callback,
							})
						);
						return;
					}
					dispatch(
						WorkerAssignmentActions.getWorkerAssignmentListRequest({
							...newBaseData,
							...newFranchiseSearchTypes,
							...omit(['searchTypes', 'franchiseSearchTypes'], baseData),
							exportExcel: false,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.MSLP: {
					dispatch(
						MSelectLodgingPopupActions.getMSelectLodgingPopupListRequest({
							...baseData,
						})
					);
					break;
				}

				case SearchFilterTypes.OI2_01: {
					dispatch(
						ForeignUserActions.getForeignUserListRequest({
							...baseData,
							callback,
						})
					);
					break;
				}

				case SearchFilterTypes.OR1_01: {
					dispatch(
						ForeignApplicantActions.getForeignApplicantListRequest({
							...baseData,
							callback,
						})
					);
					break;
				}

				case SearchFilterTypes.OP1_01: {
					dispatch(
						ForeignProgramActions.getForeignProgramListRequest(baseData)
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.CM2_01: {
					dispatch(
						ForeignSystemUnitActions.getSystemUnitListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.ST1_01: {
					dispatch(
						WorkerUseTipActions.getWorkerUseTipListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.SM1_01: {
					dispatch(
						SystemUseTipActions.getSystemUseTipListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.PA3_01: {
					if (baseData?.allData) {
						dispatch(
							WorkerPublicSalaryActions.getPublicSalaryExcelListRequest({
								...baseData,
								callback,
							})
						);
						return;
					}

					dispatch(
						WorkerPublicSalaryActions.getPublicSalaryListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.AU1_01: {
					dispatch(
						WorkerAuditMonitoringActions.getWorkerAuditMonitoringListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.DEV_01: {
					dispatch(
						DeviceMobileActions.getDeviceMobileListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.DEV_03: {
					dispatch(
						DeviceTabletActions.getDeviceTabletListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				case SearchFilterTypes.DEV_08: {
					dispatch(
						DeviceLogActions.getDeviceLogListRequest({
							...baseData,
						})
					);
					callback?.();
					break;
				}

				default:
					break;
			}
		},
		[searchFilterType, pagination, initialParams, pathname]
	);

	return {
		onSubmit,
	};
};
